import { useEffect, useMemo, useState } from "react";
import {
  aspectRatioToClass,
  getAspectRatioFromDimensions,
  getAspectRatioSectionTitle,
  loadMediaDimensionsFromUrl,
  RATIO_GROUP_ORDER,
  type MetaAspectRatio,
  type MetaMediaType,
  type RatioGroupKey,
} from "@/config/contentProgramUpload";

export type RatioGroupableItem = {
  id: string;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  mediaType?: string | null;
};

export type RatioGroupSection<T extends RatioGroupableItem> = {
  key: RatioGroupKey;
  title: string;
  items: T[];
};

function toMediaType(value: string | null | undefined): MetaMediaType {
  return value === "VIDEO" ? "VIDEO" : "IMAGE";
}

function buildItemKeys<T extends RatioGroupableItem>(items: T[]): string {
  return items
    .map(
      (item) =>
        `${item.id}:${item.thumbnailUrl || item.mediaUrl || ""}:${item.mediaType || ""}`,
    )
    .join("|");
}

export function useRatioGroups<T extends RatioGroupableItem>(
  items: T[],
  fallbackRatio: MetaAspectRatio = "9:16",
) {
  const [ratiosById, setRatiosById] = useState<Map<string, RatioGroupKey>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);

  const itemKeys = buildItemKeys(items);

  useEffect(() => {
    if (!items.length) {
      setRatiosById((prev) => (prev.size === 0 ? prev : new Map()));
      setLoading((prev) => (prev ? false : prev));
      return;
    }

    let cancelled = false;
    setLoading(true);

    void Promise.all(
      items.map(async (item) => {
        const url = item.thumbnailUrl || item.mediaUrl;
        if (!url) {
          return { id: item.id, ratio: "unknown" as RatioGroupKey };
        }

        try {
          const { width, height } = await loadMediaDimensionsFromUrl(
            url,
            toMediaType(item.mediaType),
          );
          const ratio = getAspectRatioFromDimensions(
            width,
            height,
            fallbackRatio,
          );
          return { id: item.id, ratio };
        } catch {
          return { id: item.id, ratio: fallbackRatio };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const map = new Map<string, RatioGroupKey>();
      for (const result of results) {
        map.set(result.id, result.ratio);
      }
      setRatiosById(map);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // itemKeys is a stable serialized fingerprint; avoid depending on `items` reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKeys, fallbackRatio]);

  const grouped = useMemo(() => {
    const groups = new Map<RatioGroupKey, T[]>();
    for (const item of items) {
      const ratio = ratiosById.get(item.id) ?? fallbackRatio;
      const bucket = groups.get(ratio) ?? [];
      bucket.push(item);
      groups.set(ratio, bucket);
    }
    return groups;
  }, [items, ratiosById, fallbackRatio]);

  const orderedGroups = useMemo<RatioGroupSection<T>[]>(
    () =>
      RATIO_GROUP_ORDER.filter((key) => (grouped.get(key)?.length ?? 0) > 0).map(
        (key) => ({
          key,
          title: getAspectRatioSectionTitle(key),
          items: grouped.get(key) ?? [],
        }),
      ),
    [grouped],
  );

  const getAspectClass = (id: string) =>
    aspectRatioToClass(
      ((ratiosById.get(id) ?? fallbackRatio) === "unknown"
        ? fallbackRatio
        : ratiosById.get(id) ?? fallbackRatio) as MetaAspectRatio,
    );

  const getRatioLabel = (id: string): RatioGroupKey =>
    ratiosById.get(id) ?? fallbackRatio;

  const getMetaRatioLabel = (id: string): MetaAspectRatio | null => {
    const ratio = getRatioLabel(id);
    return ratio === "unknown" ? null : ratio;
  };

  return {
    grouped,
    orderedGroups,
    loading,
    ratiosById,
    getAspectClass,
    getRatioLabel,
    getMetaRatioLabel,
  };
}
